import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ScanLine, ChevronRight, Zap, Info } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Organism from "@/components/Organism";
import HealthStatus from "@/components/HealthStatus";
import { OrganIcon, SectionTitle } from "@/components/Shared";
import { LEVEL_COLORS } from "@/lib/organs";
import { CENSUS_TO_UI, CENS_STATES, SYNC_PENDING } from "@/components/CensimentoFigure";
import CensusBasisPanel from "@/components/CensusBasisPanel";

export default function Home() {
  const { user } = useAuth();
  const [health, setHealth] = useState(null);
  const [organs, setOrgans] = useState([]);
  const [actions, setActions] = useState([]);
  const [company, setCompany] = useState(null);
  const [basisOrganId, setBasisOrganId] = useState(null);

  useEffect(() => {
    api.get("/health").then((r) => setHealth(r.data)).catch(() => {});
    api.get("/organs").then((r) => setOrgans(r.data)).catch(() => {});
    api.get("/actions").then((r) => setActions(r.data.filter((a) => a.status === "open"))).catch(() => {});
    api.get("/company").then((r) => setCompany(r.data)).catch(() => {});
  }, []);

  const firstName = (user?.name || "").split(" ")[0];
  const topAction = actions[0];

  return (
    <div className="max-w-6xl mx-auto px-4" data-testid="home-page">
      <div className="mb-6 fade-up">
        <p className="text-xs tracking-[0.25em] text-gray-500">BENVENUTO{firstName ? `, ${firstName.toUpperCase()}` : ""}</p>
        <h1 className="font-display text-2xl font-bold mt-1" data-testid="home-company-name">{company?.name || "…"}</h1>
        <p className="text-sm text-gray-500">{company?.sector} · {company?.jurisdiction}</p>
        <Link to="/censimento" data-testid="home-censimento-link"
          className="inline-flex items-center gap-1.5 mt-2 text-xs text-cyan-300 hover:text-cyan-200 transition-colors">
          Censimento aziendale →
        </Link>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Right on desktop, first on mobile: health + actions */}
        <div className="space-y-5 lg:order-2 min-w-0">
          {/* HealthStatus: aggregato company-level legacy (/health) — PEND-HOME-01:
              DESIGN_DECISION_REQUIRED, NON è semantica Census (CENSITA ≠ AZIENDA SANA).
              Resta legacy fino a decisione esplicita; mai trasformato in "health score" dal census. */}
          <HealthStatus health={health} large />

          <Link to="/scan" data-testid="home-scan-cta" className="btn-primary w-full !py-4 !text-base scan-glow md:hidden">
            <ScanLine size={20} /> SCANSIONA
          </Link>

          {topAction && (
            <Link to="/actions" data-testid="home-top-action" className="glass rounded-3xl p-5 block fade-up group">
              <SectionTitle>Cosa devo fare adesso</SectionTitle>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <span className="mt-1.5 w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ background: LEVEL_COLORS[topAction.level], boxShadow: `0 0 10px ${LEVEL_COLORS[topAction.level]}` }} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-100 leading-snug">{topAction.title}</p>
                    <p className="text-xs text-gray-500 mt-1">{topAction.action_required}</p>
                  </div>
                </div>
                <ChevronRight size={18} className="text-gray-600 group-hover:text-cyan-300 transition-colors shrink-0" />
              </div>
            </Link>
          )}

          <div className="glass rounded-3xl p-5 fade-up">
            <div className="flex items-center justify-between mb-3">
              <SectionTitle>Oggi · Azioni aperte</SectionTitle>
              <Link to="/actions" data-testid="home-all-actions" className="text-xs text-cyan-300 hover:text-cyan-200 -mt-3">Vedi tutte</Link>
            </div>
            <div className="space-y-2.5">
              {actions.slice(0, 3).map((a) => (
                <Link to="/actions" key={a.action_id} data-testid={`home-action-${a.action_id}`}
                  className="flex items-center gap-3 rounded-2xl px-3 py-2.5 hover:bg-white/[0.03] transition-colors">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: LEVEL_COLORS[a.level] }} />
                  <span className="text-sm text-gray-300 truncate">{a.title}</span>
                </Link>
              ))}
              {actions.length === 0 && (
                <p className="text-sm text-gray-500 flex items-center gap-2"><Zap size={14} /> Oggi non è richiesta alcuna azione urgente.</p>
              )}
            </div>
          </div>
        </div>

        {/* Organism */}
        <div className="glass rounded-3xl p-5 lg:order-1 fade-up min-w-0" data-testid="organism-panel">
          <SectionTitle>Organismo aziendale · tocca un organo</SectionTitle>
          <Organism organs={organs} />
          <div className="hidden lg:grid grid-cols-2 gap-2 mt-4">
            {organs.map((o) => (
              <Link key={o.organ_id} to={o.route} data-testid={`organ-link-${o.organ_id}`}
                className="flex items-center gap-2.5 rounded-2xl px-3 py-2 hover:bg-white/[0.03] transition-colors">
                <OrganIcon icon={o.icon} size={16} />
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-gray-200 truncate">{o.business_name || o.business_function}</div>
                  <div className="text-[10px] text-gray-500 truncate">{o.anatomical_name}</div>
                </div>
                <span className="ml-auto w-2 h-2 rounded-full shrink-0" data-testid={`home-organ-dot-${o.organ_id}`}
                  style={{ background: (CENS_STATES[CENSUS_TO_UI[o.census_state]] || SYNC_PENDING).color }} />
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Organ cards — mobile supplement */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6" data-testid="organ-cards">
        {organs.map((o, i) => {
          // PEND-PW-01 fase 2: display source = proiezione census (MAI legacy status/coverage,
          // mai fallback silenzioso — GO §9: census_* assente → SYNC_PENDING esplicito)
          const st = CENS_STATES[CENSUS_TO_UI[o.census_state]] || SYNC_PENDING;
          const cov = o.census_coverage ?? null;  // UNKNOWN ≠ 0%
          return (
            <div key={o.organ_id} className="relative">
              <Link to={o.route} data-testid={`organ-card-${o.organ_id}`}
                className="glass rounded-3xl p-4 fade-up hover:border-cyan-400/30 transition-colors block h-full"
                style={{ animationDelay: `${i * 40}ms` }}>
                <div className="flex items-center justify-between mb-3">
                  <span className="w-9 h-9 rounded-2xl flex items-center justify-center text-cyan-300"
                    style={{ background: "rgba(0,229,255,0.08)", border: "1px solid rgba(0,229,255,0.15)" }}>
                    <OrganIcon icon={o.icon} size={18} />
                  </span>
                  <span data-testid={`home-organ-state-${o.organ_id}`}
                    className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-medium"
                    style={{ color: st.color, background: `${st.color}18`, border: `1px solid ${st.color}35` }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: st.color }} />
                    {st.label}
                  </span>
                </div>
                <div className="text-sm font-bold font-display">{o.business_name || o.business_function}</div>
                <div className="text-xs text-gray-500">{o.anatomical_name}</div>
                <div className="mt-3 h-1 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${cov ?? 0}%`, background: "linear-gradient(90deg,#3B82F6,#00E5FF)" }} />
                </div>
                <div className="text-[10px] text-gray-600 mt-1" data-testid={`home-organ-coverage-${o.organ_id}`}>
                  Copertura {cov == null ? "—" : `${Math.round(cov)}%`}
                  {o.census_qualified ? <span className="text-amber-400 font-medium" data-testid={`home-organ-qualified-${o.organ_id}`}> · qualificata</span> : ""}
                </div>
              </Link>
              <button type="button" aria-label="Perché questa percentuale?"
                data-testid={`home-basis-open-${o.organ_id}`}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setBasisOrganId(o.organ_id); }}
                className="absolute bottom-3 right-3 w-6 h-6 rounded-full flex items-center justify-center text-gray-500 hover:text-cyan-300 transition-colors z-10"
                style={{ background: "rgba(11,17,32,0.85)", border: "1px solid rgba(0,229,255,0.15)" }}>
                <Info size={11} />
              </button>
            </div>
          );
        })}
      </div>

      {basisOrganId && (
        <CensusBasisPanel organId={basisOrganId}
          organName={organs.find((x) => x.organ_id === basisOrganId)?.business_name}
          onClose={() => setBasisOrganId(null)} />
      )}
    </div>
  );
}
